/**
 * Authentication Feature Constants
 * Routes and configuration values specific to the authentication flow
 */

/**
 * Routes that require authentication and should redirect to login on logout.
 * Paths are compared to the normalized path (leading /mk stripped when present).
 * /account covers all /account/* (my-account, wishlist, points-benefits, etc.) and
 * /account-switch (same-origin membership-switch deep link; matched by /account prefix).
 */
export const PROTECTED_ROUTES = [
  '/account',
  '/account-switch',
  '/stays/book',
  '/my-account',
  '/my-trips',
  '/points-benefits',
  '/credits-benefits',
  '/wishlist',
  '/wish-list',
  '/rci-exchanges',
] as const

/** Helper to check if a route requires authentication */
export const isProtectedRoute = (path: string): boolean => PROTECTED_ROUTES.some((route) => path.startsWith(route))

/** Login paths that are not valid redirect targets */
export const LOGIN_PATHS = ['/mk/login', '/login'] as const

/** Check if a path is a login page */
export const isLoginPath = (path: string): boolean => LOGIN_PATHS.some((loginPath) => loginPath === path)

/**
 * Storage Cleanup Constants
 * Keys that need to be cleared during logout to ensure proper session cleanup
 */

/** Okta-related keys stored in localStorage */
export const OKTA_STORAGE_KEYS = ['okta-cache', 'okta-shared-transaction', 'okta_app_state', 'okta-token-storage', 'okta-token-storage-keys'] as const

/** Session storage items that need to be cleared during logout */
export const SESSION_STORAGE_CLEANUP_KEYS = ['mfaSetupData', 'okta-auth-js-redirect-uri'] as const

/** Account context cache keys that persist user-specific data */
export const ACCOUNT_CONTEXT_CACHE_KEYS = [
  'mk_account_upcoming_vacations',
  'mk_account_past_vacations',
  'mk_account_waitlist',
  'mk_account_waitlist_resorts',
  'mk_account_wishlist',
  'mk_account_cache_timestamp',
  'mk_account_jwt_token',
  'mk_account_cached_user',
] as const

export const COOKIE_NAMES = {
  USER_NAME: 'user_name',
  ACCESS_CODE: 'access_code',
}

/**
 * Utility function to extract the apex domain from a hostname
 * Used for setting cookie domains to work across subdomains
 */
export const getApexDomain = (hostname: string): string | undefined => {
  if (!hostname) return undefined
  const host = hostname.toLowerCase()

  if (host === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(host)) {
    return 'localhost'
  }

  const parts = host.split('.')
  if (parts.length < 2) return undefined

  const lastTwo = parts.slice(-2).join('.')
  const secondLevelTLDs = new Set(['co.uk', 'org.uk', 'gov.uk', 'ac.uk', 'com.au', 'net.au', 'org.au', 'co.nz'])
  if (secondLevelTLDs.has(lastTwo)) {
    if (parts.length < 3) return undefined
    return parts.slice(-3).join('.')
  }

  return lastTwo
}

export const OKTA_ACCESS_DENIED_ERROR = 'User is not assigned to the client application'
export const OKTA_ACCESS_DENIED_ERROR_TEXT = 'Your account is not authorized to access this application. Please contact your administrator.'
export const OKTA_ACCESS_DENIED_ERROR_TEXT_EBAC =
  'This portal is for Eddie Bauer Adventure Club accounts. To access your benefits and plan your next trip, please head over to your club website. If the issue continues, contact an Eddie Bauer Adventure Club representative at 866-271-6192 for assistance.'
export const OKTA_ACCESS_DENIED_ALT_ERROR_TEXT = 'Access denied. Please check your credentials and try again.'
export const OKTA_INVALID_SCOPE_ERROR_TEXT = 'Invalid application scope. Please contact your administrator.'
export const OKTA_SERVER_ERROR_TEXT = 'Authentication server error. Please try again later.'
